import { Injectable, OnDestroy, inject } from '@angular/core';
import { RemitoStateService } from './remito-state';
import { STORAGE_KEYS } from '../constants/storage-keys';

@Injectable({ providedIn: 'root' })
export class SessionLifecycleService implements OnDestroy {
  private readonly remitoState = inject(RemitoStateService);
  private initialized = false;
  private sessionId: string | null = null;
  private isNewSession = false;

  private readonly handlePageHide = () => {
    this.unregisterSession();
  };

  init(): void {
    if (this.initialized || !this.isBrowserEnvironment()) {
      return;
    }

    this.initialized = true;
    this.sessionId = this.ensureSessionId();
    this.ensureSessionRegistration();

    window.addEventListener('pagehide', this.handlePageHide);
  }

  ngOnDestroy(): void {
    this.destroy();
  }

  destroy(): void {
    if (!this.initialized || !this.isBrowserEnvironment()) {
      return;
    }

    window.removeEventListener('pagehide', this.handlePageHide);
    this.unregisterSession();
    this.initialized = false;
  }

  private ensureSessionRegistration(): void {
    const activeSessions = this.readActiveSessions();
    const alreadyTracked = this.sessionId ? activeSessions.includes(this.sessionId) : false;
    const wasEmpty = activeSessions.length === 0;

    if (this.sessionId && !alreadyTracked) {
      activeSessions.push(this.sessionId);
      this.writeActiveSessions(activeSessions);
    }

    if (this.isNewSession && wasEmpty) {
      this.clearPersistentState();
    }
  }

  private unregisterSession(): void {
    if (!this.sessionId || !this.isBrowserEnvironment()) {
      return;
    }

    const activeSessions = this.readActiveSessions();
    const filtered = activeSessions.filter((id) => id !== this.sessionId);
    this.writeActiveSessions(filtered);
  }

  private clearPersistentState(): void {
    this.remitoState.reset({ clearCheckIn: true });
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(STORAGE_KEYS.flowStage);
    }
  }

  private ensureSessionId(): string | null {
    if (typeof sessionStorage === 'undefined') {
      this.isNewSession = false;
      return null;
    }

    const current = sessionStorage.getItem(STORAGE_KEYS.sessionId);
    if (current) {
      this.isNewSession = false;
      return current;
    }

    const newId = this.generateSessionId();
    sessionStorage.setItem(STORAGE_KEYS.sessionId, newId);
    this.isNewSession = true;
    return newId;
  }

  private generateSessionId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }

  private readActiveSessions(): string[] {
    if (!this.isBrowserEnvironment() || typeof localStorage === 'undefined') {
      return [];
    }

    try {
      const raw = localStorage.getItem(STORAGE_KEYS.activeSessions);
      if (!raw) {
        return [];
      }
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed.filter((id): id is string => typeof id === 'string');
      }
      return [];
    } catch {
      return [];
    }
  }

  private writeActiveSessions(ids: string[]): void {
    if (!this.isBrowserEnvironment() || typeof localStorage === 'undefined') {
      return;
    }
    if (!ids.length) {
      localStorage.removeItem(STORAGE_KEYS.activeSessions);
      return;
    }
    localStorage.setItem(STORAGE_KEYS.activeSessions, JSON.stringify(ids));
  }

  private isBrowserEnvironment(): boolean {
    return typeof window !== 'undefined';
  }

  getSessionId(): string | null {
    return this.sessionId;
  }
}
